/**
 * DraftCard — shared outreach draft component (Sprint 7.6 Bloque A).
 * Dumb / presentational. Hooks live in module-specific wrappers
 * (leads/LeadDraftCard, projects/ProjectDraftCardConnected).
 */
import { useState, type CSSProperties } from 'react';
import { Button } from '../ui/Button.js';
import { Icon } from '../Icon.js';
import { Pill } from '../PillGroup.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { timeAgo, htmlToText } from './utils.js';
import type { NormalizedDraft, DraftCardLabels, MutationState } from './types.js';

const LABEL_STYLE: CSSProperties = {
  fontSize: 'var(--evo-fs-caption)',
  fontWeight: 'var(--evo-fw-medium)',
  fontFamily: 'var(--evo-font)',
  letterSpacing: 'var(--evo-ls-caps)',
  textTransform: 'uppercase',
  color: 'var(--evo-fg-3)',
  margin: 0,
};

interface DraftCardProps {
  draft: NormalizedDraft;
  labels: DraftCardLabels;
  // Callbacks — undefined = acción no disponible
  onSend?: () => void;
  onCancel?: () => void;
  onSkip?: () => void;
  onUpdate?: (patch: { subject: string; bodyHtml: string; bodyText: string }) => void;
  onReply?: (body: { subject: string; bodyHtml: string; bodyText: string; inReplyTo?: string; references?: string[] }) => void;
  onRegenerate?: () => void;
  onFollowup?: () => void;
  onRetry?: () => void;
  // Estado de mutaciones (para loading/error indicators)
  sendState?: MutationState;
  cancelState?: MutationState;
  skipState?: MutationState;
  updateState?: MutationState;
  replyState?: MutationState;
  regenerateState?: MutationState;
  followupState?: MutationState;
  // Opciones visuales
  isObsolete?: boolean;
  useRichTextEditor?: boolean;  // true=Leads, false=Projects (default false)
}

export function DraftCard({
  draft,
  labels,
  onSend,
  onCancel,
  onSkip,
  onUpdate,
  onReply,
  onRegenerate,
  onFollowup,
  sendState,
  cancelState,
  skipState,
  updateState,
  replyState,
  regenerateState,
  followupState,
  isObsolete = false,
  useRichTextEditor = false,
}: DraftCardProps) {
  const [expanded, setExpanded] = useState(false);
  // Bitácora V2: la entrada colapsa a una línea; pendiente de revisión o
  // fallada arranca abierta porque pide acción.
  const [open, setOpen] = useState(draft.status === 'pending_review' || draft.status === 'failed');
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');

  const sendPending = sendState?.isPending ?? false;
  const updatePending = updateState?.isPending ?? false;
  const replyPending = replyState?.isPending ?? false;

  const typeLabel = (() => {
    switch (draft.draftType) {
      case 'initial': return labels.initial;
      case 'followup': return labels.followupN.replace('{n}', String(draft.sequenceOrder ?? ''));
      case 'new': return labels.newEmail;
      case 'brief_request': return labels.briefRequest;
      case 'content_plan_review': return labels.contentPlanReview;
      case 'design_review': return labels.designReview;
      case 'estimate_review': return labels.estimateReview ?? draft.draftType;
      case 'invoice_review': return labels.invoiceReview ?? draft.draftType;
      case 'payment_reminder': return labels.paymentReminder ?? draft.draftType;
      case 'reply': return labels.replyType;
      default: return draft.draftType;
    }
  })();

  const isTerminal = draft.status === 'sent' || draft.status === 'cancelled' || draft.status === 'skipped';
  const isFailed = draft.status === 'failed';
  const isHighlightedType = draft.draftType === 'initial' || draft.draftType === 'new';

  function copy() {
    void navigator.clipboard.writeText(`${draft.subject}\n\n${draft.bodyText}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function startEdit() {
    setEditSubject(draft.subject);
    setEditBody(useRichTextEditor ? draft.bodyHtml : draft.bodyText);
    setEditing(true);
  }

  function saveEdit() {
    if (useRichTextEditor) {
      const bodyText = htmlToText(editBody);
      if (!editSubject.trim() || !bodyText.trim()) return;
      onUpdate?.({ subject: editSubject.trim(), bodyHtml: editBody, bodyText });
    } else {
      if (!editSubject.trim() || !editBody.trim()) return;
      // Convert plain text → simple HTML for callers that lack a rich editor
      const html = editBody
        .split(/\n{2,}/)
        .map(b =>
          `<p>${b
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>')
          }</p>`,
        )
        .join('\n');
      onUpdate?.({ subject: editSubject.trim(), bodyHtml: html, bodyText: editBody });
    }
  }

  // Close edit panel automatically when update succeeds (caller resets via state)
  // We rely on caller resetting updateState through onUpdate's mutation lifecycle.
  // To avoid stale editing state when update succeeds, watch updateState.isPending → false transition.
  // Simpler: caller can leave editing open or close via remount. Mirror legacy: close on save when no error.
  // We approximate by closing if no error after save click via onSuccess callback wiring upstream.
  // Here, we close on save click when not pending and no error reported.

  const statusEl = (() => {
    if (draft.status === 'sent') {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
        {labels.sentAgo.replace('{time}', timeAgo(draft.sentAt ?? 0))}
      </span>;
    }
    if (draft.status === 'cancelled') {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0, letterSpacing: '0.4px' }}>
        {labels.cancelledStatus ?? 'CANCELLED'}
      </span>;
    }
    if (draft.status === 'skipped') {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0, letterSpacing: '0.4px' }}>
        {labels.skippedStatus ?? 'SKIPPED'}
      </span>;
    }
    if (draft.status === 'failed') {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', flexShrink: 0, fontWeight: 'var(--evo-fw-semibold)' }}>
        {labels.failedStatus ?? 'FAILED'}
      </span>;
    }
    if (draft.isDue === 1 && draft.draftType === 'followup') {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font)', flexShrink: 0, fontWeight: 'var(--evo-fw-bold)', letterSpacing: '0.4px' }}>
        {labels.dueNow ?? ''}
      </span>;
    }
    if (draft.draftType === 'followup' && draft.scheduledFor) {
      return <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-amber)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
        {labels.scheduledForLabel.replace('{date}', new Date(draft.scheduledFor).toLocaleDateString())}
      </span>;
    }
    return null;
  })();

  const actionEls = (() => {
    if (isTerminal) return null;
    if (isFailed) {
      return <>
        {onSend && <Button variant="outline" tone="cyan" size="sm" loading={sendPending} onClick={onSend}>{sendPending ? labels.sendingLabel : labels.retryBtn}</Button>}
        {onCancel && <Button variant="outline" tone="amber" size="sm" onClick={onCancel}>{labels.cancelBtn}</Button>}
      </>;
    }
    const pending = draft.status === 'pending_review';
    const editBtn = onUpdate ? <Button key="edit" variant="ghost" tone="muted" size="sm" onClick={startEdit}>{labels.editBtn}</Button> : null;

    // Lead-specific: initial draft, pending → Send | Edit | Skip
    if (draft.draftType === 'initial' && pending) {
      return <>
        {onSend && <Button variant="outline" tone="cyan" size="sm" loading={sendPending} onClick={onSend}>{sendPending ? labels.sendingLabel : labels.sendBtn}</Button>}
        {editBtn}
        {onSkip && <Button variant="ghost" tone="muted" size="sm" onClick={onSkip}>{labels.skipBtn}</Button>}
      </>;
    }
    // Lead followup, pending, no schedule → Edit | Cancel (only in lead context: isDue is defined)
    if (draft.draftType === 'followup' && pending && draft.isDue !== undefined && !draft.scheduledFor) {
      return <>{editBtn}{onCancel && <Button variant="outline" tone="amber" size="sm" onClick={onCancel}>{labels.cancelBtn}</Button>}</>;
    }
    // Lead followup, pending, due → Send Now | Edit | Skip | Cancel
    if (draft.draftType === 'followup' && pending && draft.isDue === 1) {
      return <>
        {onSend && <Button variant="outline" tone="cyan" size="sm" loading={sendPending} onClick={onSend}>{sendPending ? labels.sendingLabel : labels.sendNow}</Button>}
        {editBtn}
        {onSkip && <Button variant="ghost" tone="muted" size="sm" onClick={onSkip}>{labels.skipBtn}</Button>}
        {onCancel && <Button variant="outline" tone="amber" size="sm" onClick={onCancel}>{labels.cancelBtn}</Button>}
      </>;
    }
    // Lead followup, pending, scheduled → Send (outline) | Edit | Skip | Cancel
    if (draft.draftType === 'followup' && pending && draft.scheduledFor) {
      return <>
        {onSend && <Button variant="outline" tone="cyan" size="sm" loading={sendPending} onClick={onSend}>{sendPending ? labels.sendingLabel : labels.sendBtn}</Button>}
        {editBtn}
        {onSkip && <Button variant="ghost" tone="muted" size="sm" onClick={onSkip}>{labels.skipBtn}</Button>}
        {onCancel && <Button variant="outline" tone="amber" size="sm" onClick={onCancel}>{labels.cancelBtn}</Button>}
      </>;
    }
    // Project drafts (new/followup/brief_request/content_plan_review/reply) pending → Edit | Skip | Regenerate | Send
    if (pending) {
      return <>
        {editBtn}
        {onSkip && <Button key="skip" variant="ghost" tone="muted" size="sm" onClick={onSkip}>{labels.skipBtn}</Button>}
        {onRegenerate && <Button key="regen" variant="ghost" tone="muted" size="sm" loading={regenerateState?.isPending ?? false} onClick={onRegenerate}>{labels.regenerateBtn ?? 'Regenerate'}</Button>}
        {onSend && <Button variant="outline" tone="cyan" size="sm" loading={sendPending} onClick={onSend}>{sendPending ? labels.sendingLabel : labels.sendBtn}</Button>}
      </>;
    }
    return null;
  })();

  const sendError = sendState?.error ?? null;
  const cancelError = cancelState?.error ?? null;
  const skipError = skipState?.error ?? null;
  const hasMutationError = (sendState?.isError || cancelState?.isError || skipState?.isError || regenerateState?.isError || followupState?.isError) ?? false;

  return (
    <div style={{ border: `1px solid ${draft.isDue === 1 ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`, borderRadius: 'var(--evo-r)', overflow: 'hidden', opacity: isObsolete ? 0.55 : 1 }}>
      {isObsolete && !isTerminal && labels.followupObsolete && (
        <div style={{ padding: '6px 16px', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--evo-warn)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)' }}>
            {labels.followupObsolete}
          </span>
        </div>
      )}
      {/* "Ready to send" banner — cyan tint 8% (no token equivalent: rgba(6,182,212,0.08); reportado) */}
      {draft.isDue === 1 && !isTerminal && labels.readyBanner && (
        <div style={{ padding: '5px 16px', background: 'rgba(6,182,212,0.08)', borderBottom: '1px solid var(--evo-cyan)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: 'var(--evo-fs-tiny)', fontWeight: 'var(--evo-fw-bold)', letterSpacing: '0.6px', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font)' }}>
            {labels.readyBanner}
          </span>
          {draft.scheduledFor && labels.scheduledWasLabel && (
            <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
              — {labels.scheduledWasLabel.replace('{time}', timeAgo(draft.scheduledFor))}
            </span>
          )}
        </div>
      )}
      {/* Entrada de bitácora V2: disco del canal · tipo · asunto · estado · chevron.
          Saliente = disco solo contorno (--evo-ch-email). */}
      <div
        onClick={() => setOpen(v => !v)}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg-hover)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg)'; }}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--evo-log-icon-gap)',
          minHeight: 'var(--evo-log-row-min-h)', boxSizing: 'border-box',
          padding: 'var(--evo-log-row-py) var(--evo-log-row-px)',
          background: 'var(--evo-log-row-bg)', cursor: 'pointer', transition: 'background 80ms',
        }}
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
          width: 'var(--evo-log-icon-sz)', height: 'var(--evo-log-icon-sz)', flexShrink: 0,
          borderRadius: 'var(--evo-r-pill)', border: '1px solid var(--evo-ch-email)',
          background: 'transparent', color: 'var(--evo-ch-email)',
        }}>
          <Icon name="email" size={13} />
        </span>
        <span style={{
          flexShrink: 0, width: 'var(--evo-log-label-w)', fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {typeLabel}
        </span>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)',
          color: isTerminal ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {draft.subject}
        </span>
        {draft.generatedByClaude === 0 && labels.writtenByHand ? (
          <Pill variant="outline" tone="muted" size="sm">{labels.writtenByHand}</Pill>
        ) : null}
        {statusEl}
        <span style={{
          flexShrink: 0, display: 'inline-flex', color: 'var(--evo-fg-4)',
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms',
        }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>
      {open && actionEls && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px var(--evo-log-row-px) 0' }}>
          {actionEls}
        </div>
      )}

      {open && hasMutationError && (
        <div style={{ padding: '6px 16px', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--evo-warn)', color: 'var(--evo-warn)', fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)' }}>
          {(sendError ?? cancelError ?? skipError ?? regenerateState?.error ?? followupState?.error)?.message ?? labels.actionFailed}
        </div>
      )}
      {open && draft.status === 'failed' && draft.sendError && (
        <div style={{ padding: '6px 16px', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--evo-warn)', color: 'var(--evo-warn)', fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {draft.sendError.slice(0, 120)}
        </div>
      )}

      {open && draft.draftType === 'followup' && draft.status === 'pending_review' && !draft.scheduledFor && labels.willSchedule && (
        <div style={{ padding: '6px 16px', background: 'var(--evo-bg-1)', borderBottom: '1px solid var(--evo-stroke)' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            {labels.willSchedule}
          </span>
        </div>
      )}

      {!open ? null : editing ? (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <p style={{ ...LABEL_STYLE, marginBottom: '6px' }}>{labels.editSubjectLabel}</p>
            <Input
              value={editSubject}
              onChange={setEditSubject}
              fullWidth
            />
          </div>
          <div>
            <p style={{ ...LABEL_STYLE, marginBottom: '6px' }}>{labels.editBodyLabel}</p>
            {useRichTextEditor ? (
              <RichTextEditor
                value={editBody}
                onChange={setEditBody}
                placeholder={labels.composerPlaceholder}
                minHeight={200}
              />
            ) : (
              <Textarea
                value={editBody}
                onChange={setEditBody}
                rows={8}
                fullWidth
              />
            )}
          </div>
          {updateState?.isError && (
            <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', margin: 0 }}>
              {updateState.error?.message ?? labels.actionFailed}
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--evo-stroke)', paddingTop: '10px' }}>
            <Button
              variant="outline" tone="neutral" size="sm"
              onClick={() => setEditing(false)}
            >
              {labels.editCancel}
            </Button>
            <Button
              variant="outline" tone="neutral" size="sm"
              loading={updatePending}
              disabled={!editSubject.trim() || !(useRichTextEditor ? htmlToText(editBody).trim() : editBody.trim()) || updatePending}
              onClick={() => {
                const bodyValid = useRichTextEditor ? htmlToText(editBody).trim() : editBody.trim();
                if (!editSubject.trim() || !bodyValid || updatePending) return;
                saveEdit();
                setEditing(false);
              }}
            >
              {labels.editSave}
            </Button>
            {onSend && (() => {
              const editDisabled = !editSubject.trim() || !(useRichTextEditor ? htmlToText(editBody).trim() : editBody.trim()) || updatePending || (sendState?.isPending ?? false);
              return (
                <Button
                  variant="solid" tone="cyan" size="sm"
                  disabled={editDisabled}
                  onClick={() => {
                    const bodyValid = useRichTextEditor ? htmlToText(editBody).trim() : editBody.trim();
                    if (!editSubject.trim() || !bodyValid || updatePending) return;
                    saveEdit();
                    onSend();
                    setEditing(false);
                  }}
                >
                  {labels.sendNow}
                </Button>
              );
            })()}
          </div>
        </div>
      ) : (!isTerminal || draft.status === 'sent' || isFailed) ? (
        <div style={{ padding: '14px 16px' }}>
          <p style={{
            fontSize: 'var(--evo-list-title-fs)', lineHeight: 1.55, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            overflow: 'hidden', maxHeight: expanded ? 'none' : '4.65em',
          }}>
            {draft.bodyText}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
            <Button variant="link" tone="muted" size="sm" onClick={() => setExpanded(prev => !prev)}>
              {expanded ? labels.collapseBtn : labels.expandBtn}
            </Button>
            <Button variant="outline" tone={copied ? 'cyan' : 'muted'} size="sm" onClick={copy}>
              {copied ? labels.copied : labels.copyBtn}
            </Button>
          </div>
          {draft.status === 'sent' && labels.sentBodyNote && (
            <p style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: '8px 0 0', fontStyle: 'italic' }}>
              {labels.sentBodyNote}
            </p>
          )}
        </div>
      ) : null}

      {open && draft.status === 'sent' && (onReply || onFollowup) && (
        <div style={{ borderTop: '1px solid var(--evo-stroke)', padding: '10px 16px' }}>
          {!replyOpen ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              {onReply && <Button variant="outline" tone="cyan" size="sm" onClick={() => setReplyOpen(true)}>{labels.replyBtn}</Button>}
              {onFollowup && <Button variant="ghost" tone="muted" size="sm" loading={followupState?.isPending ?? false} onClick={onFollowup}>{labels.followupBtn ?? 'Follow-up'}</Button>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {useRichTextEditor ? (
                <RichTextEditor
                  value={replyBody}
                  onChange={setReplyBody}
                  placeholder={labels.composerPlaceholder}
                  minHeight={140}
                />
              ) : (
                <Textarea
                  value={replyBody}
                  onChange={setReplyBody}
                  rows={5}
                  placeholder={labels.composerPlaceholder}
                  fullWidth
                />
              )}
              {replyState?.isError && (
                <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', margin: 0 }}>
                  {replyState.error?.message ?? labels.actionFailed}
                </p>
              )}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button
                  variant="outline" tone="cyan" size="sm"
                  loading={replyPending}
                  onClick={() => {
                    const text = useRichTextEditor ? htmlToText(replyBody) : replyBody.trim();
                    if (!text.trim()) return;
                    const reSubject = draft.subject.startsWith('Re:') ? draft.subject : `Re: ${draft.subject}`;
                    const html = useRichTextEditor ? replyBody : `<p>${text}</p>`;
                    onReply?.({
                      subject: reSubject,
                      bodyHtml: html,
                      bodyText: text,
                      ...(draft.sentMessageId ? { inReplyTo: draft.sentMessageId, references: [draft.sentMessageId] } : {}),
                    });
                    // Optimistic close; if error, state stays visible via replyState
                    setReplyOpen(false);
                    setReplyBody('');
                  }}
                >
                  {replyPending ? labels.sendingLabel : labels.sendReplyBtn}
                </Button>
                <Button
                  variant="ghost" tone="muted" size="sm"
                  onClick={() => { setReplyOpen(false); setReplyBody(''); replyState?.reset?.(); }}
                >
                  {labels.editCancel}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
